/**
 * ToolCallItem — 工具调用胶囊卡片 + 结果展示。
 * ToolCallsBlock — 多个 ToolCallItem 的容器。
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Check, CheckCircle, Zap, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AnimatedCollapse } from '../../../shared/components/animated-collapse';
import { ToolResultView } from '../../tool-transforms';
import { createLogger } from '../../../../utils/logger';
import { getOpen, setOpen as persistOpen } from './collapse-state';
import { CAPSULE_ICON_MAP } from './tool-capsule-match';
import type { CapsuleMatchType } from './tool-capsule-match';
import './tool-call-item.less';

const log = createLogger('ToolCapsule');

/* ── ToolCallItem ── */

interface ToolCallProps {
	id?: string;
	label: string;
	paramLabel?: string;
	result?: string;
	status?: 'running' | 'done' | 'error';
	matchType?: CapsuleMatchType;
}

/** param-inline 展开时的 max-width（px）。仅用于驱动 CSS transition，取足够大的值避免截断 */
const INLINE_MAX_WIDTH = 600;

export function ToolCallItem({ tool }: { tool: ToolCallProps }) {
	const stableKey = tool.id ? `tool:${tool.id}` : undefined;
	const [open, setOpen] = useState(() => (stableKey ? getOpen(stableKey) : false));
	const [showInline, setShowInline] = useState(() => (stableKey ? !getOpen(stableKey) : true));
	const inlineTimer = useRef<ReturnType<typeof setTimeout>>();

	const isRunning = tool.status === 'running';
	const isDone = tool.status === 'done' || tool.status === 'error';
	const hasResult = isDone && !!tool.result;

	// 特殊胶囊匹配：使用专属图标
	const MatchedIcon = tool.matchType ? CAPSULE_ICON_MAP[tool.matchType] : undefined;
	const matchClass = tool.matchType ? `tool-capsule--${tool.matchType}` : undefined;

	const handleToggle = useCallback(() => {
		if (!hasResult) return;
		const next = !open;
		log.info(`[toggle] open=${open}→${next}`);

		clearTimeout(inlineTimer.current);
		if (next) {
			setShowInline(false);
		} else {
			setShowInline(true);
		}
		setOpen(next);
		if (stableKey) persistOpen(stableKey, next);
	}, [hasResult, open, stableKey]);

	useEffect(() => () => clearTimeout(inlineTimer.current), []);

	const outerLabel = tool.status === 'error' ? '失败' : isDone ? '已完成' : undefined;

	return (
		<div>
			<div className="tool-capsule__row">
				<button
					className={cn(
						'tool-capsule',
						matchClass,
						!matchClass && isRunning && 'tool-capsule--running',
						!matchClass && tool.status === 'error' && 'tool-capsule--error',
						!matchClass && tool.status === 'done' && 'tool-capsule--done',
						!matchClass && !isDone && !isRunning && 'tool-capsule--idle',
						matchClass && isRunning && 'tool-capsule--match-running',
						hasResult ? 'tool-capsule--clickable' : 'tool-capsule--static',
					)}
					disabled={!hasResult}
					onClick={handleToggle}
					type="button"
				>
					<div className="tool-capsule__title">
						<span className="shrink-0">
							{isRunning ? (
								MatchedIcon ? (
									<MatchedIcon className="h-3 w-3 animate-pulse" />
								) : (
									<Loader2 className="h-3 w-3 text-muted-foreground/50 animate-spin" />
								)
							) : tool.status === 'error' ? (
								<Zap className="h-3 w-3 text-destructive" />
							) : MatchedIcon ? (
								<MatchedIcon className="h-3 w-3 animate-in zoom-in duration-200" />
							) : (
								<Check className="h-3 w-3 shrink-0 text-primary animate-in zoom-in duration-200" />
							)}
						</span>
						<span className={cn('tool-capsule__label', isRunning && 'tool-capsule__label--breathing')}>{tool.label}</span>
						{tool.paramLabel && (
							<span
								className="tool-capsule__param-inline"
								style={{
									maxWidth: showInline ? INLINE_MAX_WIDTH : 0,
									opacity: showInline ? 1 : 0,
								}}
							>
								{tool.paramLabel}
							</span>
						)}
						{hasResult && <ChevronRight className={cn('h-3 w-3 shrink-0 text-muted-foreground/40 transition-transform duration-200', open && 'rotate-90')} />}
					</div>
					{tool.paramLabel && (
						<AnimatedCollapse open={open}>
							<div className="tool-capsule__param-full">
								<span>{tool.paramLabel}</span>
							</div>
						</AnimatedCollapse>
					)}
				</button>
				{isRunning && <Loader2 className="h-3 w-3 shrink-0 text-muted-foreground/40 animate-spin" />}
				{outerLabel &&
					(tool.matchType && tool.status === 'done' ? (
						<CheckCircle className={cn('h-3.5 w-3.5 shrink-0 animate-in fade-in slide-in-from-left-1 duration-300', `tool-capsule__check--${tool.matchType}`)} />
					) : (
						<span
							className={cn(
								'tool-capsule__status animate-in fade-in slide-in-from-left-1 duration-300',
								tool.status === 'error' ? 'tool-capsule__status--error' : 'tool-capsule__status--done',
							)}
						>
							{outerLabel}
						</span>
					))}
			</div>
			<AnimatedCollapse open={open}>{tool.result && <ToolResultView rawResult={tool.result} toolLabel={tool.label} />}</AnimatedCollapse>
		</div>
	);
}

/* ── ToolCallsBlock ── */

export function ToolCallsBlock({ tools }: { tools: ToolCallProps[] }) {
	return (
		<div className="flex max-w-[85%] flex-col gap-0.5 animate-in fade-in slide-in-from-left-2 duration-300">
			{tools.map((t, i) => (
				<ToolCallItem key={t.id ?? i} tool={t} />
			))}
		</div>
	);
}
